import React from 'react';
import { FaEnvelope, FaFacebook, FaGlobe, FaInstagram, FaPhone } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const normalizeUrl = (value, platform = 'general') => {
  if (!value) {
    return null;
  }

  const trimmedValue = `${value}`.trim();

  if (!trimmedValue) {
    return null;
  }

  if (trimmedValue.startsWith('http://') || trimmedValue.startsWith('https://')) {
    return trimmedValue;
  }

  if (trimmedValue.startsWith('www.')) {
    return `https://${trimmedValue}`;
  }

  if (trimmedValue.startsWith('@')) {
    const handle = trimmedValue.slice(1).trim();
    if (!handle) {
      return null;
    }

    return platform === 'facebook'
      ? `https://www.facebook.com/${handle}`
      : `https://www.instagram.com/${handle}`;
  }

  const normalizedHandle = trimmedValue.replace(/^instagram\.com\//i, '').replace(/^facebook\.com\//i, '').replace(/^\//, '').replace(/\/$/, '');
  if (normalizedHandle && !normalizedHandle.includes(' ')) {
    return platform === 'facebook'
      ? `https://www.facebook.com/${normalizedHandle}`
      : `https://www.instagram.com/${normalizedHandle}`;
  }

  return null;
};

const renderLinks = (value, linkClassName, textClassName, platform = 'general') => {
  if (!value) {
    return null;
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const normalizedUrl = normalizeUrl(item, platform);

      if (!normalizedUrl) {
        return (
          <span key={item} className={textClassName}>
            {item}
          </span>
        );
      }

      return (
        <a
          key={item}
          href={normalizedUrl}
          target="_blank"
          rel="noreferrer"
          className={linkClassName}
        >
          {item}
        </a>
      );
    });
};

const SellerShowcaseCard = ({ seller, className = '', showMapButton = true }) => {
  if (!seller) {
    return null;
  }

  const hasSocialLinks = seller.homepage || seller.facebook || seller.instagram;
  const publicEmail = seller.publicEmail || seller.email;

  return (
    <article className={`w-full overflow-hidden rounded-[30px] border-4 border-blue-500 bg-[#fffaf1]/95 shadow-xl ${className}`.trim()}>
      <div className="relative overflow-hidden border-b-4 border-blue-500 bg-[linear-gradient(135deg,#163b73_0%,#1e63b5_45%,#f5b940_100%)] px-6 py-8 text-white lg:px-8">
        <div className="absolute inset-y-0 right-0 w-1/3 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.28),transparent_58%)]" />
        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-[0.4em] text-white/80">Markkinamyyjä</p>
          <h2 className="mt-4 max-w-5xl [overflow-wrap:anywhere] xs:text-sm sm:text-sm text-lg font-black leading-tight text-white lg:text-3xl">
            {seller.name}
          </h2>

          {seller.spotNames?.length || showMapButton ? (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {seller.spotNames?.map((spotName) => (
                <span
                  key={`${seller.companyID}-${spotName}`}
                  className="inline-flex min-w-[110px] items-center justify-center rounded-full border border-white/70 bg-slate-900/30 px-5 py-2 text-center text-sm font-bold tracking-[0.08em] text-white backdrop-blur-sm"
                >
                  Paikka {spotName}
                </span>
              ))}

              {showMapButton ? (
                <Link
                  to="/myyntipaikat-kartta"
                  state={{
                    hideSpotInfo: true,
                    publicSellerMap: true,
                    sellerMapView: true,
                    sellerCompanyID: seller.companyID,
                    sellerName: seller.name,
                    sellerSpotNames: seller.spotNames ?? [],
                  }}
                  className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-md transition hover:bg-blue-700"
                >
                  Näytä kartalla
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="px-6 py-6 lg:px-8">
        <section className="rounded-2xl bg-white/85 p-5 ring-1 ring-slate-200">
          <div className="mb-3 text-sm font-bold uppercase tracking-[0.22em] text-slate-500">Esittely</div>
          <p className="whitespace-pre-line [overflow-wrap:anywhere] text-lg leading-8 text-slate-700">
            {seller.description || 'Myyjältä ei ole lisätty esittelytekstiä.'}
          </p>
        </section>

        <section className="mt-5 rounded-[24px] bg-slate-800/90 p-5 text-slate-100 shadow-lg ring-1 ring-slate-600/50">
          <div className="text-sm font-bold uppercase tracking-[0.28em] text-sky-200">Yhteydet</div>

          <div className="mt-5 grid grid-cols-1 gap-4 text-sm md:grid-cols-2 xl:grid-cols-2">
            {publicEmail ? (
              <div className="rounded-2xl bg-white/14 p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold text-white">
                  <FaEnvelope className="text-sky-300" />
                  Sähköposti
                </div>
                <a href={`mailto:${publicEmail}`} className="break-all [overflow-wrap:anywhere] text-sky-200 underline underline-offset-2">
                  {publicEmail}
                </a>
              </div>
            ) : null}

            {seller.phone ? (
              <div className="rounded-2xl bg-white/14 p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold text-white">
                  <FaPhone className="text-sky-300" />
                  Puhelin
                </div>
                <a href={`tel:${seller.phone.replace(/\s+/g, '')}`} className="[overflow-wrap:anywhere] text-sky-200 underline underline-offset-2">
                  {seller.phone}
                </a>
              </div>
            ) : null}

            {seller.homepage ? (
              <div className="rounded-2xl bg-white/14 p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold text-white">
                  <FaGlobe className="text-sky-300" />
                  Kotisivu
                </div>
                <div>
                  {renderLinks(
                    seller.homepage,
                    'inline-block mr-3 break-all text-sky-200 underline underline-offset-2',
                    'inline-block mr-3 break-all text-slate-100'
                  )}
                </div>
              </div>
            ) : null}

            {seller.facebook ? (
              <div className="rounded-2xl bg-white/14 p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold text-white">
                  <FaFacebook className="text-sky-300" />
                  Facebook
                </div>
                <div>
                  {renderLinks(
                    seller.facebook,
                    'inline-block mr-3 break-all text-sky-200 underline underline-offset-2',
                    'inline-block mr-3 break-all text-slate-100',
                    'facebook'
                  )}
                </div>
              </div>
            ) : null}

            {seller.instagram ? (
              <div className="rounded-2xl bg-white/14 p-4">
                <div className="mb-2 flex items-center gap-2 font-semibold text-white">
                  <FaInstagram className="text-sky-300" />
                  Instagram
                </div>
                <div>
                  {renderLinks(
                    seller.instagram,
                    'inline-block mr-3 break-all text-sky-200 underline underline-offset-2',
                    'inline-block mr-3 break-all text-slate-100'
                  )}
                </div>
              </div>
            ) : null}

            {!publicEmail && !seller.phone && !hasSocialLinks ? (
              <p className="rounded-2xl bg-white/14 p-4 text-slate-300">Ei julkisia yhteystietoja tai verkkolinkkejä.</p>
            ) : null}
          </div>
        </section>
      </div>
    </article>
  );
};

export default SellerShowcaseCard;